import React, { useEffect, useRef, useState, createElement } from 'react';
import moment from 'moment';
import { View, Text, Pressable } from '../lib/react-native-compat';
import { pNum } from '../db/company';
import { round2 } from './billingRates';
import { fmtPct } from './billingFuel';
import { tcFileDate } from './billingTimecardApproval';
import { OutlineButton } from './procurementFilters';
import BillingAiChat from './billingAiChat';
import { isRentUsage } from './billingRentalUsage';
import { laborRateFor, laborWageOf } from './billingPricing';

// Pre-flight validation for the summary backup PDF — the last look before the
// document goes to the client. The PDF button opens this checklist instead of
// exporting straight away; each area runs in turn (the step spins while its
// checks execute, then settles on a verdict) and the export only proceeds once
// the user has seen the results. Fails block the primary button — "Export
// Anyway" stays available because the person assembling the invoice sometimes
// knows better than the rule (a credit memo, a client-approved exception) —
// but the override is the loud path, never the default.
//
// The checks are pure functions over the same line objects the export itself
// consumes (live txns or the frozen snapshots summaryLines falls back to), so
// what gets validated is exactly what gets printed. Snapshot-fallback lines
// carry fewer fields (no rateAmt, no vinv match status); every check treats a
// missing field as "can't check", not "broken", so old summaries still
// validate on what they do carry.

// Levels, worst first. 'info' never moves a step off pass — it's context the
// reader should see (date range, document counts), not a problem.
export const worstLevel = (items) => ['fail', 'warn'].find((l) => items.some((x) => x.level === l)) || 'pass';

const fDate = (d) => (d ? moment.utc(d).format('M/D/YY') : '');
const money = (n) => {
    const v = pNum(n);
    const s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${v < 0 ? '-' : ''}$${s}`;
};
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

// Mirror of billing.js's exclusion rules — 513xx/514xx/516xx/517xx (burden,
// taxes, insurance) never bill, and anything outside 50000–59999 isn't a cost
// transaction at all. A line like that on a summary means the filter was
// bypassed somewhere (a stale snapshot, a hand-edited row), so it's a fail.
const NEVER_BILLED_CT_PREFIX = ['513', '514', '516', '517'];

// A line's one-glance identity in a finding: date · who/desc · cost.
const lineRef = (t) => [fDate(t.date), t.who || t.eq || t.ds || t.cc, money(t.cost)].filter(Boolean).join(' · ');

// ── 1 · Line eligibility ──
// Rows that shouldn't be on a client summary at all.
export const checkEligibility = (rows) => {
    const items = [];
    const nb = rows.filter((t) => t.nb);
    if (nb.length) {
        items.push({
            level: 'fail',
            text: `${plural(nb.length, 'not-billable line')} on the summary (${money(nb.reduce((s, t) => s + t.cost, 0))} cost)`,
            lines: nb.map(lineRef),
        });
    }
    const noRate = rows.filter((t) => t.noRate);
    if (noRate.length) {
        items.push({
            level: 'fail',
            text: `${plural(noRate.length, 'labor line')} with no rate — the sheet can't price them, they'd bill $0`,
            lines: noRate.map(lineRef),
        });
    }
    const neverBilled = rows.filter((t) => t.ct && NEVER_BILLED_CT_PREFIX.some((p) => String(t.ct).startsWith(p)));
    if (neverBilled.length) {
        items.push({
            level: 'fail',
            text: `${plural(neverBilled.length, 'line')} on never-billed cost types (burden/taxes/insurance)`,
            lines: neverBilled.map((t) => `${t.ct} · ${lineRef(t)}`),
        });
    }
    const outOfRange = rows.filter((t) => t.ct && (String(t.ct) < '50000' || String(t.ct) >= '60000'));
    if (outOfRange.length) {
        items.push({
            level: 'fail',
            text: `${plural(outOfRange.length, 'line')} outside the 50000-series cost types (revenue/overhead)`,
            lines: outOfRange.map((t) => `${t.ct} · ${lineRef(t)}`),
        });
    }
    const seen = {};
    rows.forEach((t) => { if (t.key) seen[t.key] = (seen[t.key] || 0) + 1; });
    const dups = Object.entries(seen).filter(([, n]) => n > 1);
    if (dups.length) {
        items.push({
            level: 'fail',
            text: `${plural(dups.length, 'transaction')} appearing more than once on the summary`,
        });
    }
    // Deliberate inclusions the reader should still glance at.
    const ccOverride = rows.filter((t) => t.ccNb && !t.nb);
    if (ccOverride.length) {
        items.push({
            level: 'warn',
            text: `${plural(ccOverride.length, 'line')} on a non-billable cost code, included via billable override`,
            lines: ccOverride.map((t) => `${t.cc} · ${lineRef(t)}`),
        });
    }
    const reversed = rows.filter((t) => t.vinv === 'Reversed');
    if (reversed.length) {
        items.push({
            level: 'warn',
            text: `${plural(reversed.length, 'line')} whose vendor invoice was reversed in JDE, included via billable override`,
            lines: reversed.map(lineRef),
        });
    }
    const credits = rows.filter((t) => pNum(t.cost) < 0);
    if (credits.length) {
        items.push({
            level: 'warn',
            text: `${plural(credits.length, 'credit line')} (negative cost, ${money(credits.reduce((s, t) => s + t.cost, 0))}) — confirm the client should see them`,
            lines: credits.map(lineRef),
        });
    }
    if (!items.length) items.push({ level: 'info', text: `All ${plural(rows.length, 'line')} are billable cost transactions` });
    return items;
};

// ── 2 · Rates & amounts ──
// Every line's bill amount recomputed from its own basis, plus drift against
// the current rate sheet / markup settings (drift is a warn: a frozen
// snapshot is allowed to disagree with today's sheet, but the exporter
// should know it does).
export const checkRates = (rows, ctx = {}) => {
    const { settings, equipBills, rateCfg } = ctx;
    const items = [];
    const off = (msg, list) => { if (list.length) items.push({ level: 'fail', text: `${plural(list.length, 'line')} ${msg}`, lines: list.map((x) => x.ref) }); };

    const laborST_OT = rows.filter((t) => !t.nb && (t.ct === '51100' || t.ct === '51200'));
    off('of ST/OT labor billing by markup instead of a sheet rate', laborST_OT
        .filter((t) => !t.hourly).map((t) => ({ ref: lineRef(t) })));
    off('of ST/OT labor with no rate sheet classification (Bills As is blank)', laborST_OT
        .filter((t) => t.hourly && !t.ba).map((t) => ({ ref: `${t.jde || '(no JDE class)'} · ${lineRef(t)}` })));
    off('where hours × rate doesn’t match the bill amount', rows
        .filter((t) => !t.nb && t.hourly && pNum(t.rateAmt) > 0 && pNum(t.hrs) !== 0)
        .filter((t) => {
            const hourlyRate = t.rateUom && t.rateUom !== 'hr' ? pNum(t.rateAmt) / 10 : pNum(t.rateAmt);
            return Math.abs(pNum(t.bill) - round2(pNum(t.hrs) * hourlyRate)) > 0.01;
        })
        .map((t) => ({ ref: `${lineRef(t)} · ${t.hrs} hrs × ${money(t.rateAmt)}/${t.rateUom || 'hr'} ≠ ${money(t.bill)}` })));
    // Per diem and any other non-ST/OT labor passes through at cost.
    off('of labor (per diem etc.) not passing through at cost', rows
        .filter((t) => !t.nb && !t.noRate && t.cls === 'Labor' && !t.hourly && t.ct !== '51100' && t.ct !== '51200')
        .filter((t) => Math.abs(pNum(t.bill) - pNum(t.cost)) > 0.01)
        .map((t) => ({ ref: `${lineRef(t)} bills ${money(t.bill)}` })));
    // Rental vouchers are AP lines whose Total_Hours is a quantity — they must
    // never rate-bill. Rentals rate-bill through their HeavyJob usage lines.
    off('of rental vouchers (52200) billed hours × rate — a voucher quantity isn’t usage hours', rows
        .filter((t) => !t.nb && t.ct === '52200' && t.hourly && !isRentUsage(t)).map((t) => ({ ref: lineRef(t) })));
    // A rental unit nobody set up in HeavyJob has no hours to rate-bill, so
    // its voucher still bills cost + Equipment % — worth a look before it goes.
    const noHj = rows.filter((t) => !t.nb && !t.inv && t.ruMissing);
    if (noHj.length) {
        items.push({
            level: 'warn',
            text: `${plural(noHj.length, 'rental voucher line')} for units not in HeavyJob bill cost + markup, not the sheet rate — set the unit up in HeavyJob (PO-<po>-<line>) to bill its hours`,
            lines: [...new Set(noHj.map((t) => `PO ${t.po} line ${t.pol}${t.eqDs ? ` — ${t.eqDs}` : ''}`))],
        });
    }
    // Markup lines: bill = cost × (1 + mk%).
    off('where cost × markup doesn’t match the bill amount', rows
        .filter((t) => !t.nb && !t.noRate && !t.hourly && t.cls !== 'Labor')
        .filter((t) => Math.abs(pNum(t.bill) - pNum(t.cost) * (1 + pNum(t.mk) / 100)) > 0.01)
        .map((t) => ({ ref: `${lineRef(t)} · +${t.mk}% ≠ ${money(t.bill)}` })));

    // Drift against the wage — labor bills at the person's JDE wage × the
    // job's multiplier (never the sheet's target wage). A frozen line whose
    // rate isn't that, under today's multiplier, is worth a look: the
    // multiplier changed since billing, or the line was frozen at a sheet
    // rate before the actual-wage rule.
    if (rateCfg) {
        const expected = (t) => {
            const wage = t.wage > 0 ? t.wage : laborWageOf({ ct: t.ct, cost: pNum(t.cost), hrs: pNum(t.hrs), sid: t.sid, date: t.date }, null);
            return laborRateFor(t.ct, wage, rateCfg, t.ba);
        };
        const drift = laborST_OT.filter((t) => t.hourly && t.ba && pNum(t.rateAmt) > 0 && pNum(t.hrs) > 0)
            .filter((t) => {
                const cur = expected(t);
                return cur > 0 && Math.abs(cur - pNum(t.rateAmt)) > 0.005;
            });
        if (drift.length) {
            items.push({
                level: 'warn',
                text: `${plural(drift.length, 'labor line')} frozen at a rate that isn't the person's JDE wage × ${rateCfg.m}${rateCfg.otf ? ` (OT × ${rateCfg.otf})` : ''}`,
                lines: [...new Set(drift.map((t) => `${t.who || t.jde || t.ba} — billed ${money(t.rateAmt)}/hr, wage × multiplier ${money(expected(t))}/hr`))],
            });
        }
    }
    if (equipBills) {
        const drift = rows.filter((t) => !t.nb && (t.ct === '52100' || isRentUsage(t)) && t.hourly && t.tier && equipBills[t.tier])
            .filter((t) => Math.abs(pNum(equipBills[t.tier].rate) - pNum(t.rateAmt)) > 0.005);
        if (drift.length) {
            items.push({
                level: 'warn',
                text: `${plural(drift.length, 'equipment line')} frozen at a rate that differs from today's rate sheet`,
                lines: [...new Set(drift.map((t) => `${t.tier} — billed ${money(t.rateAmt)}/${t.rateUom || 'day'}, sheet now ${money(equipBills[t.tier].rate)}/${equipBills[t.tier].uom}`))],
            });
        }
    }
    if (settings?.mk) {
        // Pass-through lines bill at the job's pass-through %, not their
        // class markup, and contract at-cost lines at 0% — the Contract
        // rules step checks those instead.
        const drift = rows.filter((t) => !t.nb && !t.noRate && !t.hourly && !t.pt && t.cr?.effect !== 'cost' && t.cls !== 'Labor' && settings.mk[t.cls] != null)
            .filter((t) => pNum(t.mk) !== pNum(settings.mk[t.cls]));
        if (drift.length) {
            items.push({
                level: 'warn',
                text: `${plural(drift.length, 'line')} frozen at a markup that differs from today's settings`,
                lines: [...new Set(drift.map((t) => `${t.cls} — billed +${t.mk}%, settings now +${settings.mk[t.cls]}%`))],
            });
        }
    }
    if (!items.length) items.push({ level: 'info', text: 'Every bill amount recomputes from its own rate or markup' });
    return items;
};

// ── 3 · Backup documents ──
// `files` is the job's SharePoint timecard listing (already fetched by the
// caller). Labor date coverage uses the same convention timecardsInRange
// does: a card backs the days within six days either side of its named date
// ("WE 8-30" covers the week before it).
export const checkDocs = (rows, files) => {
    const items = [];
    const live = rows.filter((t) => !t.nb);

    // Third-party spend should carry its scanned vendor invoice. Rental
    // usage lines are card hours, not spend — their backup is the ticket.
    const thirdParty = live.filter((t) => ((t.cls !== 'Labor' && t.cls !== 'Equipment') || t.ct === '52200') && !isRentUsage(t));
    const noDoc = thirdParty.filter((t) => !t.pdf);
    if (noDoc.length) {
        const byVendor = {};
        noDoc.forEach((t) => {
            const k = t.who || '(no vendor)';
            const b = byVendor[k] || (byVendor[k] = { n: 0, cost: 0 });
            b.n += 1; b.cost += pNum(t.cost);
        });
        items.push({
            level: 'warn',
            text: `${plural(noDoc.length, 'third-party line')} with no scanned vendor invoice behind them (${money(noDoc.reduce((s, t) => s + pNum(t.cost), 0))})`,
            lines: Object.entries(byVendor)
                .sort((a, b) => b[1].cost - a[1].cost)
                .map(([who, b]) => `${who} — ${plural(b.n, 'line')}, ${money(b.cost)}`),
        });
    }
    const partial = live.filter((t) => t.vinv === 'Partial');
    if (partial.length) {
        items.push({
            level: 'warn',
            text: `${plural(partial.length, 'PO line')} only partially vouchered — the invoice behind them may not be final`,
            lines: partial.map((t) => `PO ${t.po} · ${lineRef(t)}`),
        });
    }

    // Timecard coverage for the labor days.
    const laborDates = [...new Set(live.filter((t) => t.cls === 'Labor' && t.date).map((t) => String(t.date).slice(0, 10)))].sort();
    if (laborDates.length) {
        if (files === null) {
            items.push({ level: 'warn', text: 'Couldn’t list the job’s SharePoint timecards — labor coverage not verified' });
        } else {
            const cardDates = (files || []).map((f) => tcFileDate(f)).filter(Boolean);
            const covered = (d) => cardDates.some((fd) => Math.abs(moment.utc(fd).diff(moment.utc(d), 'days')) <= 6);
            const uncovered = laborDates.filter((d) => !covered(d));
            if (uncovered.length) {
                items.push({
                    level: 'warn',
                    text: `${plural(uncovered.length, 'labor day')} with no timecard on SharePoint to back them`,
                    lines: uncovered.map(fDate),
                });
            } else {
                items.push({ level: 'info', text: `${plural(cardDates.length, 'timecard')} on SharePoint cover all ${plural(laborDates.length, 'labor day')}` });
            }
        }
    }
    const docCount = new Set(thirdParty.filter((t) => t.pdf).map((t) => t.pdf)).size;
    if (docCount) items.push({ level: 'info', text: `${plural(docCount, 'distinct vendor invoice')} will bind into the backup` });
    if (!items.length) items.push({ level: 'info', text: 'No third-party or labor lines needing backup documents' });
    return items;
};

// ── 4 · Totals & hygiene ──
export const checkTotals = (rows, ctx = {}) => {
    const items = [];
    const live = rows.filter((t) => !t.nb);
    // The equipment fuel surcharge bills on top of the lines (billingFuel),
    // and the stored summary total carries it.
    const fuelAmt = pNum(ctx.fuel?.amt);
    // Plain Number, not db/company's pNum: that one truncates to the cent,
    // and 700 × 1.15 lands a hair under 805 in binary — three such lines read
    // three cents short of the stored total, which sums the raw products.
    const bill = live.reduce((s, t) => s + (Number(t.bill) || 0), 0) + fuelAmt;
    if (ctx.fuel?.missing?.length) {
        items.push({
            level: 'warn',
            text: `No Gulf Coast diesel price entered for ${ctx.fuel.missing.join(', ')} — equipment worked in the following quarter carries no fuel surcharge. Enter it under Rates & Rules → Fuel Surcharge.`,
        });
    }
    if (fuelAmt) {
        items.push({
            level: 'info',
            text: `Fuel surcharge ${money(fuelAmt)} on ${money(ctx.fuel.base)} of equipment (${ctx.fuel.periods.filter((p) => p.amt).map((p) => `${fmtPct(p.pct)} for ${p.span}`).join('; ')})`,
        });
    }
    if (ctx.storedTotal != null && Math.abs(pNum(ctx.storedTotal) - bill) > 0.01) {
        items.push({
            level: 'fail',
            text: `Recomputed total ${money(bill)} doesn't match the stored summary total ${money(ctx.storedTotal)}`,
        });
    }
    const flagged = rows.filter((t) => t.rs === 'Flagged');
    if (flagged.length) {
        items.push({
            level: 'fail',
            text: `${plural(flagged.length, 'line')} still flagged for review`,
            lines: flagged.map(lineRef),
        });
    }
    const noDate = rows.filter((t) => !t.date);
    if (noDate.length) {
        items.push({ level: 'warn', text: `${plural(noDate.length, 'line')} with no work date`, lines: noDate.map(lineRef) });
    }
    if (ctx.clientName === '') {
        items.push({ level: 'warn', text: 'No client on the job master — FTMS will need the client picked by hand' });
    }
    const dates = rows.map((t) => t.date).filter(Boolean).sort();
    if (dates.length) {
        const span = moment.utc(dates[dates.length - 1]).diff(moment.utc(dates[0]), 'days') + 1;
        items.push({
            level: 'info',
            text: `Lines dated ${fDate(dates[0])} – ${fDate(dates[dates.length - 1])} (${plural(span, 'day')}) · ${plural(live.length, 'line')} · bills ${money(bill)}`,
        });
    }
    return items;
};

// ── Contract rules ──
// The job's billable rubric (billingContract) applied to the summary as a
// whole: lines the contract bills at cost that carry a markup anyway, lines
// it puts inside the rates that someone overrode onto the summary, lines it
// flags for a call, and fleet equipment billed under the Service Order's
// minimum daily charge. `ctx.contract` is the job's contract profile
// (contractFor) — null when the rules are off on this job.
export const checkContract = (rows, ctx = {}) => {
    const c = ctx.contract;
    if (!c) return [{ level: 'info', text: 'Contract rules are off on this job — billability follows the cost codes alone' }];
    const items = [];
    const markedUp = rows.filter((t) => !t.nb && t.cr?.effect === 'cost' && !t.hourly && pNum(t.mk) !== 0);
    if (markedUp.length) {
        items.push({
            level: 'fail',
            text: `${plural(markedUp.length, 'line')} the contract bills at cost carrying a markup (${money(markedUp.reduce((s, t) => s + pNum(t.bill) - pNum(t.cost), 0))} over cost)`,
            lines: markedUp.map((t) => `${t.cr.label} · ${lineRef(t)} · +${t.mk}%`),
        });
    }
    const overridden = rows.filter((t) => !t.nb && t.cr?.effect === 'nb');
    if (overridden.length) {
        items.push({
            level: 'warn',
            text: `${plural(overridden.length, 'line')} the contract puts inside the rates, on the summary via billable override`,
            lines: overridden.map((t) => `${t.cr.label} · ${lineRef(t)}`),
        });
    }
    const review = {};
    rows.filter((t) => !t.nb && t.cr?.effect === 'warn').forEach((t) => {
        (review[t.cr.id] = review[t.cr.id] || { rule: t.cr, list: [] }).list.push(t);
    });
    Object.values(review).forEach(({ rule, list }) => {
        items.push({
            level: 'warn',
            text: `${plural(list.length, 'line')} — ${rule.label} (${money(list.reduce((s, t) => s + pNum(t.cost), 0))} cost)`,
            lines: list.map(lineRef),
        });
    });
    // Minimum daily charge: every piece of equipment assigned — fleet or
    // rented — bills at least the standby minimum a day. A unit's day can
    // split across cost codes, so hours sum per unit per work date first.
    const min = pNum(c.standbyHrs);
    if (min > 0) {
        const days = {};
        rows.filter((t) => !t.nb && (t.ct === '52100' || isRentUsage(t)) && t.unit && t.date).forEach((t) => {
            const k = `${t.unit}|${t.date}`;
            const d = days[k] || (days[k] = { unit: t.unit, date: t.date, hrs: 0, hourly: 0 });
            d.hrs += pNum(t.hrs);
            if (!d.hourly && pNum(t.rateAmt) > 0) d.hourly = t.rateUom && t.rateUom !== 'hr' ? pNum(t.rateAmt) / 10 : pNum(t.rateAmt);
        });
        const list = Object.values(days);
        const short = list.filter((d) => d.hrs > 0 && d.hrs < min)
            .sort((a, b) => a.unit.localeCompare(b.unit) || a.date.localeCompare(b.date));
        if (short.length) {
            const more = short.reduce((s, d) => s + (min - d.hrs) * d.hourly, 0);
            items.push({
                level: 'warn',
                text: `${plural(short.length, 'equipment day')} billed under the ${min}-hour minimum daily charge — ${round2(short.reduce((s, d) => s + (min - d.hrs), 0))} more hours${more ? ` (${money(more)})` : ''} are billable`,
                lines: short.map((d) => `${d.unit} · ${fDate(d.date)} · ${round2(d.hrs)} hrs billed, minimum ${min}`),
            });
        }
        // PB4: a unit sitting at the minimum ten business days running has
        // to be offered to the Company for demob. A weekday with no hours
        // at all breaks the run — the unit may not have been on site.
        if (c.standbyDays) {
            const byUnit = {};
            list.forEach((d) => { (byUnit[d.unit] = byUnit[d.unit] || {})[d.date] = d.hrs; });
            const idle = [];
            Object.entries(byUnit).forEach(([unit, map]) => {
                const dates = Object.keys(map).sort();
                let run = 0;
                let best = 0;
                const cur = moment.utc(dates[0]);
                const end = moment.utc(dates[dates.length - 1]);
                while (cur.isSameOrBefore(end)) {
                    const dow = cur.day();
                    if (dow !== 0 && dow !== 6) {
                        const h = map[cur.format('YYYY-MM-DD')];
                        run = h != null && h <= min ? run + 1 : 0;
                        best = Math.max(best, run);
                    }
                    cur.add(1, 'day');
                }
                if (best >= c.standbyDays) idle.push(`${unit} · ${best} business days at or under ${min} hrs`);
            });
            if (idle.length) {
                items.push({
                    level: 'warn',
                    text: `${plural(idle.length, 'unit')} on standby ${c.standbyDays}+ business days in a row — the Service Order says offer the client a demob`,
                    lines: idle,
                });
            }
        }
    }
    if (!items.length) items.push({ level: 'info', text: `Every line bills the way the ${c.name} contract says it should` });
    return items;
};

// The checklist, in the order the modal walks it. `docs` is the only step
// that reaches out (the SharePoint timecard listing comes in through
// ctx.loadTimecards); `labor` reads ctx.laborQa; everything else is pure
// over the rows.
export const EXPORT_CHECKS = [
    { id: 'lines', title: 'Line eligibility', desc: 'No not-billable, no-rate, burden or duplicate rows', run: (rows) => checkEligibility(rows) },
    { id: 'rates', title: 'Rates & amounts', desc: 'Every bill amount recomputes; drift vs today’s sheet', run: (rows, ctx) => checkRates(rows, ctx) },
    // The summary's labor QA (billingQa summaryQa), worked out by the caller
    // over the job's whole labor history and handed in as ready items.
    {
        id: 'labor',
        title: 'Labor QA',
        desc: 'Phase codes, classification, vs earlier days, per diem',
        run: (rows, ctx) => (ctx.laborQa && ctx.laborQa.length
            ? ctx.laborQa
            : [{ level: 'info', text: 'No labor lines on this summary to check' }]),
    },
    {
        id: 'docs',
        title: 'Backup documents',
        desc: 'Vendor invoices behind the spend, timecards behind the labor',
        run: async (rows, ctx) => {
            let files = null;
            try { files = ctx.loadTimecards ? await ctx.loadTimecards() : []; } catch (e) { files = null; }
            return checkDocs(rows, files);
        },
    },
    { id: 'contract', title: 'Contract rules', desc: 'At-cost lines, overrides, review flags, equipment minimums', run: (rows, ctx) => checkContract(rows, ctx) },
    { id: 'totals', title: 'Totals & review', desc: 'Stored total, flagged lines, client, date range', run: (rows, ctx) => checkTotals(rows, ctx) },
];

const TONE = {
    pass: { color: '#15803d', glyph: '✓', label: 'Pass' },
    warn: { color: '#b45309', glyph: '!', label: 'Check' },
    fail: { color: '#b91c1c', glyph: '✕', label: 'Fail' },
    info: { color: '#64748b', glyph: '·', label: '' },
};

// Each step holds its spinner at least this long so the cascade reads as
// deliberate checking, not a flicker. Zero under jest — the tests walk
// through the modal on their way to the export and shouldn't sit through
// the choreography.
const STEP_MIN_MS = process.env.NODE_ENV === 'test' ? 0 : 550;

const Spin = ({ light } = {}) => createElement('span', {
    style: {
        display: 'inline-block', width: light ? 12 : 14, height: light ? 12 : 14, borderRadius: '50%',
        border: light ? '2px solid rgba(255,255,255,0.35)' : '2px solid #cbd5e1',
        borderTopColor: light ? '#fff' : '#0f172a',
        animation: 'bexpspin 0.7s linear infinite',
    },
});

// The pre-export checklist modal. Runs the checks top to bottom on mount,
// then hands control back: onProceed fires the actual export, onCancel just
// closes. `rows`/`tag` are exactly what downloadSummaryPdf would get.
const BillingExportCheck = ({ rows, tag, ctx, ai, onCancel, onProceed }) => {
    // One entry per check: null = not started, 'run' = spinning, or the
    // finished { status, items }.
    const [steps, setSteps] = useState(() => EXPORT_CHECKS.map(() => null));
    const [exporting, setExporting] = useState(false);
    // Bumped when the assistant applies a change: `rows` has moved, so the
    // checklist that judged the old rows has to run again or the verdict on
    // screen is about a document that no longer exists.
    const [pass, setPass] = useState(0);
    const alive = useRef(true);
    useEffect(() => () => { alive.current = false; }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // A re-run starts from a clean slate — leaving the previous
            // verdicts up while the cascade walks back down them would show
            // an old fail beside a line that has just been fixed.
            if (pass) setSteps(EXPORT_CHECKS.map(() => null));
            for (let i = 0; i < EXPORT_CHECKS.length; i += 1) {
                if (cancelled) return;
                setSteps((p) => p.map((s, k) => (k === i ? 'run' : s)));
                const t0 = Date.now();
                let items;
                try {
                    items = await EXPORT_CHECKS[i].run(rows, ctx);
                } catch (e) {
                    items = [{ level: 'fail', text: `Check errored — ${e?.message || e}` }];
                }
                const wait = STEP_MIN_MS - (Date.now() - t0);
                if (wait > 0) await new Promise((r) => setTimeout(r, wait));
                if (cancelled) return;
                const result = { status: worstLevel(items), items };
                setSteps((p) => p.map((s, k) => (k === i ? result : s)));
            }
        })();
        return () => { cancelled = true; };
        // One pass per open, plus one more each time an applied revision
        // moves the lines underneath it.
        // eslint-disable-next-line
    }, [pass]);

    const done = steps.every((s) => s && s !== 'run');
    const worst = done ? (['fail', 'warn'].find((l) => steps.some((s) => s.status === l)) || 'pass') : null;

    const doExport = async () => {
        if (exporting) return;
        setExporting(true);
        try { await onProceed(); } finally { if (alive.current) setExporting(false); }
    };

    return (
        <Pressable
            onPress={onCancel}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
                backgroundColor: 'rgba(15, 23, 42, 0.5)', alignItems: 'center', justifyContent: 'center',
                cursor: 'default', padding: 24,
            }}
        >
            {createElement('style', null, '@keyframes bexpspin { to { transform: rotate(360deg); } }')}
            <Pressable
                onPress={(e) => e.stopPropagation && e.stopPropagation()}
                style={{
                    backgroundColor: '#fff', borderRadius: 8, width: ai ? 760 : 640, maxWidth: '96vw', maxHeight: '88vh',
                    overflowY: 'auto', boxShadow: '0 20px 60px rgba(15, 23, 42, 0.3)', cursor: 'default',
                }}
            >
                <View style={{ paddingTop: 16, paddingBottom: 12, paddingLeft: 22, paddingRight: 22, borderBottomWidth: 1, borderColor: '#e2e8f0', flexDirection: 'row', alignItems: 'center' }}>
                    <View style={{ flexGrow: 1 }}>
                        <Text style={{ fontSize: 16, fontWeight: '700', color: '#0f172a' }}>Pre-Export Checks{tag ? ` — ${tag}` : ''}</Text>
                        <Text style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            Validating {plural(rows.length, 'line')} before the backup PDF goes to the client
                        </Text>
                    </View>
                    <Pressable onPress={onCancel}>
                        <Text style={{ fontSize: 18, color: '#64748b' }}>×</Text>
                    </Pressable>
                </View>

                <View style={{ paddingTop: 14, paddingBottom: 8, paddingLeft: 22, paddingRight: 22 }}>
                    {EXPORT_CHECKS.map((c, i) => {
                        const s = steps[i];
                        const tone = s && s !== 'run' ? TONE[s.status] : null;
                        return (
                            <View key={c.id} style={{ flexDirection: 'row', marginBottom: i === EXPORT_CHECKS.length - 1 ? 0 : 14, opacity: s ? 1 : 0.4 }}>
                                <View style={{ width: 26, alignItems: 'center', paddingTop: 1 }}>
                                    {s === 'run' ? <Spin /> : tone ? (
                                        <View style={{ width: 18, height: 18, borderRadius: 9, backgroundColor: tone.color, alignItems: 'center', justifyContent: 'center' }}>
                                            <Text style={{ fontSize: 11, color: '#fff', fontWeight: '700', lineHeight: 18 }}>{tone.glyph}</Text>
                                        </View>
                                    ) : (
                                        <View style={{ width: 18, height: 18, borderRadius: 9, borderWidth: 2, borderColor: '#cbd5e1', alignItems: 'center', justifyContent: 'center' }}>
                                            <Text style={{ fontSize: 10, color: '#94a3b8', fontWeight: '700', lineHeight: 14 }}>{i + 1}</Text>
                                        </View>
                                    )}
                                </View>
                                <View style={{ flexGrow: 1, flexShrink: 1, paddingLeft: 8 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
                                        <Text style={{ fontSize: 13, fontWeight: '600', color: '#0f172a' }}>{c.title}</Text>
                                        {tone && tone.label ? (
                                            <Text style={{ fontSize: 11, fontWeight: '700', color: tone.color, marginLeft: 8 }}>{tone.label.toUpperCase()}</Text>
                                        ) : null}
                                    </View>
                                    {!s || s === 'run' ? (
                                        <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{s === 'run' ? 'Checking…' : c.desc}</Text>
                                    ) : (
                                        s.items.map((it, k) => (
                                            <View key={k} style={{ marginTop: 3 }}>
                                                <Text style={{ fontSize: 11.5, color: TONE[it.level].color, fontWeight: it.level === 'fail' ? '600' : '400' }}>
                                                    {it.level === 'info' ? '' : `${TONE[it.level].glyph} `}{it.text}
                                                </Text>
                                                {(it.lines || []).slice(0, 8).map((l, j) => (
                                                    <Text key={j} style={{ fontSize: 10.5, color: '#64748b', paddingLeft: 12, marginTop: 1 }} numberOfLines={1}>{l}</Text>
                                                ))}
                                                {(it.lines || []).length > 8 ? (
                                                    <Text style={{ fontSize: 10.5, color: '#94a3b8', paddingLeft: 12, marginTop: 1 }}>+ {it.lines.length - 8} more</Text>
                                                ) : null}
                                            </View>
                                        ))
                                    )}
                                </View>
                            </View>
                        );
                    })}
                </View>

                {/* The revision desk. It reads the same lines the checks
                    just judged (and the verdicts themselves), so "what's
                    wrong with this one" is answered from the document in
                    front of both of you rather than from a fresh look. */}
                {ai ? (
                    <View style={{ paddingTop: 4, paddingBottom: 6, paddingLeft: 22, paddingRight: 22, marginTop: 8 }}>
                        <BillingAiChat
                            section="billingSummary"
                            title={`Revise ${tag || 'this summary'}`}
                            hint="e.g. drop the freight lines, or what would the client push back on?"
                            context={() => ai.context(done ? steps.map((s, i) => ({
                                area: EXPORT_CHECKS[i].title,
                                verdict: s.status,
                                items: s.items.filter((x) => x.level !== 'info').map((x) => x.text),
                            })).filter((x) => x.items.length || x.verdict !== 'pass') : null)}
                            actions={ai.actions}
                            locked={ai.locked}
                            onApplied={() => setPass((n) => n + 1)}
                        />
                    </View>
                ) : null}

                <View style={{ flexDirection: 'row', alignItems: 'center', paddingTop: 10, paddingBottom: 14, paddingLeft: 22, paddingRight: 22, borderTopWidth: 1, borderColor: '#e2e8f0', marginTop: 8 }}>
                    <Text style={{ fontSize: 11, color: '#64748b', flexGrow: 1, flexShrink: 1, paddingRight: 12 }}>
                        {exporting ? 'Building the backup — binding documents into the PDF…'
                            : !done ? 'Running checks…'
                            : worst === 'pass' ? 'All checks passed — good to send.'
                            : worst === 'warn' ? 'Passed with items to double-check before sending.'
                            : 'Fix the failed items, or export anyway if you know better.'}
                    </Text>
                    <OutlineButton label="Cancel" onPress={exporting ? undefined : onCancel} />
                    <View style={{ width: 10 }} />
                    <Pressable
                        onPress={done && !exporting ? doExport : undefined}
                        style={{
                            paddingTop: 5, paddingBottom: 5, paddingLeft: 14, paddingRight: 14, borderRadius: 4, marginBottom: 6,
                            backgroundColor: worst === 'fail' ? '#b91c1c' : '#0f172a',
                            opacity: done ? 1 : 0.4,
                        }}
                    >
                        {exporting ? (
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Spin light />
                                <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600', marginLeft: 7 }}>Building PDF…</Text>
                            </View>
                        ) : (
                            <Text style={{ fontSize: 12, color: '#fff', fontWeight: '600' }}>
                                {worst === 'fail' ? 'Export Anyway' : 'Export PDF'}
                            </Text>
                        )}
                    </Pressable>
                </View>
            </Pressable>
        </Pressable>
    );
};

export default BillingExportCheck;
